import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    Layout,
    LayoutChangedMeta,
    PanelSize,
    useDefaultLayout,
    usePanelRef,
} from "react-resizable-panels";
import { CoaMappingPreview } from "./buildCoaMappingPreview";

export const PREVIEW_TABLE_PANEL_ID = "coa-mapping-preview-table";
export const SOURCES_SIDEBAR_PANEL_ID = "coa-mapping-preview-sources";

const DEFAULT_SIDEBAR_SIZE = 100 / 3;
const INITIAL_LAYOUT: Layout = {
    [PREVIEW_TABLE_PANEL_ID]: 100,
    [SOURCES_SIDEBAR_PANEL_ID]: 0,
};

interface Params {
    preview: CoaMappingPreview;
    fallbackAccountId: string | null;
    escapeInterceptorRef: MutableRefObject<(() => void) | null>;
}

export function useCoaMappingPreviewSourcesSidebar({
    preview,
    fallbackAccountId,
    escapeInterceptorRef,
}: Params) {
    const panelRef = usePanelRef();
    const { defaultLayout: savedLayout, onLayoutChanged: saveLayout } =
        useDefaultLayout({
            id: "coa-mapping-preview-sources-split",
            storage: localStorage,
        });
    const lastSizeRef = useRef(
        savedLayout?.[SOURCES_SIDEBAR_PANEL_ID] ?? DEFAULT_SIDEBAR_SIZE,
    );
    const [accountId, setAccountId] = useState<string | null>(null);
    const [isShown, setIsShown] = useState(false);

    const account =
        accountId === null
            ? undefined
            : preview.accounts.find((candidate) => candidate.id === accountId);
    const isAccountMissing = accountId !== null && account === undefined;

    const show = useCallback(
        () => panelRef.current?.resize(`${lastSizeRef.current}%`),
        [panelRef],
    );
    const hide = useCallback(() => panelRef.current?.collapse(), [panelRef]);

    const viewSources = useCallback(
        (nextAccountId: string) => {
            setAccountId(nextAccountId);
            if (panelRef.current?.isCollapsed()) {
                show();
            }
        },
        [panelRef, show],
    );
    const toggle = useCallback(
        () => (isShown ? hide() : show()),
        [isShown, hide, show],
    );

    const onLayoutChanged = useCallback(
        (layout: Layout, meta: LayoutChangedMeta) => {
            const sidebarSize = layout[SOURCES_SIDEBAR_PANEL_ID];
            if (!sidebarSize) {
                return;
            }
            lastSizeRef.current = sidebarSize;
            saveLayout(layout, meta);
        },
        [saveLayout],
    );
    const onSidebarResize = useCallback(
        (size: PanelSize) => {
            const isNowShown = size.asPercentage > 0;
            setIsShown(isNowShown);
            if (isNowShown) {
                setAccountId((current) => current ?? fallbackAccountId);
            }
        },
        [fallbackAccountId],
    );

    useEffect(() => {
        if (!isAccountMissing) {
            return;
        }
        setAccountId(null);
        hide();
    }, [isAccountMissing, hide]);

    useEffect(() => {
        if (!isShown) {
            return;
        }
        escapeInterceptorRef.current = hide;
        return () => {
            escapeInterceptorRef.current = null;
        };
    }, [isShown, hide, escapeInterceptorRef]);

    return {
        panelRef,
        account,
        isShown,
        canShow: account !== undefined || fallbackAccountId !== null,
        viewSources,
        toggle,
        initialLayout: INITIAL_LAYOUT,
        onLayoutChanged,
        onSidebarResize,
    };
}
